import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Eye, Search, User, Edit3 } from 'lucide-react';
import { CustomBadge } from '@/components/ui/custom-badge';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { getFullName } from '@/utils/name-helpers';
import { glassCardStatic } from '@/utils/admin-glass-styles';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

interface Client {
  id: string;
  first_name: string | null;
  last_name: string | null;
  preferred_language: string | null;
  timezone: string | null;
  country: string | null;
  date_of_birth: string | null;
  gender: string | null;
  last_login_at: string | null;
  updated_at: string;
  email?: string;
  account_status: string;
  created_at: string;
  phone_number: string | null;
  avatar_url?: string | null;
}

interface AuthUserData {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
}

const AdminClients: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setIsLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState<boolean>(false);
  const [newEmail, setNewEmail] = useState<string>('');
  const [isUpdatingEmail, setIsUpdatingEmail] = useState<boolean>(false);
  const { toast } = useToast();
  
  const pageSize = 10;

  useEffect(() => {
    fetchClients();
  }, [page, searchQuery, statusFilter]);

  const fetchClients = async () => {
    setIsLoading(true);
    try {
      // Fetch auth users data using the new edge function
      const { data: authResponse, error: authError } = await supabase.functions.invoke('get-admin-users', {
        body: { page, perPage: pageSize }
      });

      if (authError) {
        console.error('Error fetching auth users:', authError);
        console.error('Auth Error Details:', authError);
        throw new Error(`Failed to fetch user authentication data: ${authError.message || authError}`);
      }

      const userEmailMap = new Map<string, string>();
      if (authResponse && authResponse.users) {
        authResponse.users.forEach((user: AuthUserData) => {
          userEmailMap.set(user.id, user.email);
        });
      }

      // Fetch clients data from the clients table
      let query = supabase
        .from('clients')
        .select('*', { count: 'exact' });

      if (searchQuery) {
        query = query.or(`first_name.ilike.%${searchQuery}%,last_name.ilike.%${searchQuery}%`);
      }
      
      if (statusFilter !== 'all') {
        query = query.eq('account_status', statusFilter);
      }

      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      
      const { data, count, error } = await query
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;

      const clientsWithEmail = data.map(client => ({
        ...client,
        email: userEmailMap.get(client.id) || 'Email not available'
      }));

      setClients(clientsWithEmail);
      if (count !== null) setTotalCount(count);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to fetch client data',
        description: `Please try again later. Error: ${error.message || error}`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateClientStatus = async (clientId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('clients')
        .update({ account_status: newStatus })
        .eq('id', clientId);

      if (error) throw error;

      toast({
        title: 'Update Successful',
        description: 'Client status has been updated',
      });

      setClients(clients.map(client => 
        client.id === clientId 
          ? { ...client, account_status: newStatus } 
          : client
      ));
      
      setSelectedClient(null);
    } catch (error) {
      console.error('Error updating client status:', error);
      toast({
        variant: 'destructive',
        title: 'Update Failed',
        description: 'Unable to update client status',
      });
    }
  };

  const updateUserEmail = async (userId: string, newEmail: string) => {
    setIsUpdatingEmail(true);
    try {
      const { data, error } = await supabase.functions.invoke('update-user-email', {
        body: { userId, newEmail }
      });

      if (error) {
        throw error;
      }

      toast({
        title: 'Email Updated Successfully',
        description: `Email has been changed from ${data.oldEmail} to ${data.newEmail}`,
      });

      // Update the local state
      setClients(clients.map(client => 
        client.id === userId 
          ? { ...client, email: newEmail } 
          : client
      ));

      if (selectedClient && selectedClient.id === userId) {
        setSelectedClient({ ...selectedClient, email: newEmail });
      }

      setIsEmailDialogOpen(false);
      setNewEmail('');
      
    } catch (error) {
      console.error('Error updating user email:', error);
      toast({
        variant: 'destructive',
        title: 'Email Update Failed',
        description: error.message || 'Unable to update user email',
      });
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  const handleEmailChange = (client: Client) => {
    setSelectedClient(client);
    setNewEmail(client.email || '');
    setIsEmailDialogOpen(true);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Not logged in';
    try {
      return format(new Date(dateString), 'yyyy-MM-dd HH:mm');
    } catch (error) {
      return 'Invalid date';
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <CustomBadge variant="success">Active</CustomBadge>;
      case 'suspended':
        return <CustomBadge variant="destructive">Suspended</CustomBadge>;
      case 'inactive':
        return <CustomBadge variant="outline">Inactive</CustomBadge>;
      default:
        return <CustomBadge>{status}</CustomBadge>;
    }
  };

  const getFullNameForClient = (client: Client) => {
    return getFullName(client);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Client Management</h2>
        <div className="text-sm text-gray-500">Total: {totalCount} Clients</div>
      </div>

      <Card className={glassCardStatic}>
        <CardHeader>
          <CardTitle className="text-lg">Client List</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search client name or email..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Select
              value={statusFilter}
              onValueChange={setStatusFilter}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client ID</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Registration Date</TableHead>
                      <TableHead>Last Login</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clients.length > 0 ? (
                      clients.map((client) => (
                        <TableRow key={client.id}>
                          <TableCell className="font-mono text-xs">{client.id.substring(0, 8)}...</TableCell>
                          <TableCell>{getFullNameForClient(client) || <span className="text-gray-400">Not set</span>}</TableCell>
                          <TableCell>{client.phone_number || <span className="text-gray-400">Not set</span>}</TableCell>
                          <TableCell>{client.email}</TableCell>
                          <TableCell>{getStatusBadge(client.account_status)}</TableCell>
                          <TableCell>{formatDate(client.created_at)}</TableCell>
                          <TableCell>{formatDate(client.last_login_at)}</TableCell>
                          <TableCell>
                            <div className="flex space-x-2">
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => setSelectedClient(client)}
                                  >
                                    <Eye className="h-3.5 w-3.5 mr-1" />
                                    Details
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Client Details</AlertDialogTitle>
                                    <AlertDialogDescription asChild>
                                      <div className="mt-4 space-y-4">
                                        {selectedClient && (
                                          <>
                                            <div className="flex justify-center mb-4">
                                              {selectedClient.avatar_url ? (
                                                <img 
                                                  src={selectedClient.avatar_url} 
                                                  alt={getFullName(selectedClient) || 'User avatar'} 
                                                  className="h-24 w-24 rounded-full object-cover border"
                                                />
                                              ) : (
                                                <div className="h-24 w-24 rounded-full bg-gray-200 flex items-center justify-center">
                                                  <User className="h-12 w-12 text-gray-400" />
                                                </div>
                                              )}
                                            </div>

                                            <div className="grid grid-cols-2 gap-y-2 text-sm">
                                              <div className="font-semibold">Client ID:</div>
                                              <div className="font-mono">{selectedClient.id}</div>
                                              
                                              <div className="font-semibold">Name:</div>
                                              <div>{getFullName(selectedClient) || <span className="text-gray-400">Not set</span>}</div>
                                              
                                               <div className="font-semibold">Phone:</div>
                                               <div>{selectedClient.phone_number || <span className="text-gray-400">Not set</span>}</div>
                                              
                                               <div className="font-semibold">Email:</div>
                                               <div className="flex items-center gap-2">
                                                 <span>{selectedClient.email}</span>
                                                 <Dialog open={isEmailDialogOpen} onOpenChange={setIsEmailDialogOpen}>
                                                   <DialogTrigger asChild>
                                                     <Button
                                                       variant="ghost"
                                                       size="sm"
                                                       onClick={() => handleEmailChange(selectedClient)}
                                                       className="h-6 w-6 p-0"
                                                     >
                                                       <Edit3 className="h-3 w-3" />
                                                     </Button>
                                                   </DialogTrigger>
                                                    <DialogContent className="sm:max-w-[425px]">
                                                      <DialogHeader>
                                                        <DialogTitle>Update User Email</DialogTitle>
                                                        <DialogDescription>
                                                          Updating email for {getFullName(selectedClient)}
                                                        </DialogDescription>
                                                      </DialogHeader>
                                                      <div className="grid gap-4 py-4">
                                                        <div className="grid grid-cols-4 items-center gap-4">
                                                          <label htmlFor="current-email" className="text-right text-sm font-medium">
                                                            Current Email:
                                                          </label>
                                                          <div className="col-span-3 text-sm text-gray-600">
                                                            {selectedClient.email}
                                                          </div>
                                                        </div>
                                                        <div className="grid grid-cols-4 items-center gap-4">
                                                          <label htmlFor="new-email" className="text-right text-sm font-medium">
                                                            New Email:
                                                          </label>
                                                          <Input
                                                            id="new-email"
                                                            type="email"
                                                            value={newEmail}
                                                            onChange={(e) => setNewEmail(e.target.value)}
                                                            className="col-span-3"
                                                            placeholder="Enter new email address"
                                                          />
                                                        </div>
                                                      </div>
                                                      <DialogFooter>
                                                        <Button
                                                          variant="outline"
                                                          onClick={() => {
                                                            setIsEmailDialogOpen(false);
                                                            setNewEmail('');
                                                          }}
                                                          disabled={isUpdatingEmail}
                                                        >
                                                          Cancel
                                                        </Button>
                                                        <Button
                                                          onClick={() => updateUserEmail(selectedClient.id, newEmail)}
                                                          disabled={isUpdatingEmail || !newEmail || newEmail === selectedClient.email}
                                                        >
                                                          {isUpdatingEmail ? 'Updating...' : 'Update Email'}
                                                        </Button>
                                                      </DialogFooter>
                                                    </DialogContent>
                                                 </Dialog>
                                               </div>
                                              
                                              <div className="font-semibold">Phone Number:</div>
                                              <div>{selectedClient.phone_number || <span className="text-gray-400">Not set</span>}</div>
                                              
                                              <div className="font-semibold">Account Status:</div>
                                              <div>{getStatusBadge(selectedClient.account_status)}</div>
                                              
                                              <div className="font-semibold">Registration Date:</div>
                                              <div>{formatDate(selectedClient.created_at)}</div>
                                              
                                              <div className="font-semibold">Last Login:</div>
                                              <div>{formatDate(selectedClient.last_login_at)}</div>
                                            </div>

                                            <div className="mt-6 border-t pt-4">
                                              <h4 className="font-medium mb-2">Change Account Status</h4>
                                              <div className="flex flex-wrap gap-2">
                                                <Button 
                                                  variant={selectedClient.account_status === 'active' ? 'default' : 'outline'} 
                                                  size="sm"
                                                  onClick={() => updateClientStatus(selectedClient.id, 'active')}
                                                >
                                                  Set Active
                                                </Button>
                                                <Button 
                                                  variant={selectedClient.account_status === 'suspended' ? 'default' : 'outline'} 
                                                  size="sm"
                                                  onClick={() => updateClientStatus(selectedClient.id, 'suspended')}
                                                >
                                                  Suspend Account
                                                </Button>
                                                <Button 
                                                  variant={selectedClient.account_status === 'inactive' ? 'default' : 'outline'} 
                                                  size="sm"
                                                  onClick={() => updateClientStatus(selectedClient.id, 'inactive')}
                                                >
                                                  Set Inactive
                                                </Button>
                                              </div>
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Close</AlertDialogCancel>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} className="h-24 text-center">
                          No client data found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="mt-4">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious 
                          onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                          className={page <= 1 ? 'pointer-events-none opacity-50' : ''}
                        />
                      </PaginationItem>
                      
                      {[...Array(totalPages)].map((_, i) => {
                        const pageNumber = i + 1;
                        if (
                          pageNumber === 1 ||
                          pageNumber === totalPages ||
                          (pageNumber >= page - 1 && pageNumber <= page + 1)
                        ) {
                          return (
                            <PaginationItem key={i}>
                              <PaginationLink
                                isActive={pageNumber === page}
                                onClick={() => setPage(pageNumber)}
                              >
                                {pageNumber}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        } else if (
                          (pageNumber === page - 2 && pageNumber > 1) ||
                          (pageNumber === page + 2 && pageNumber < totalPages)
                        ) {
                          return <PaginationItem key={i}>...</PaginationItem>;
                        }
                        return null;
                      })}
                      
                      <PaginationItem>
                        <PaginationNext 
                          onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                          className={page >= totalPages ? 'pointer-events-none opacity-50' : ''}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminClients;
